import type { Address } from '@/types/Address';

// Mock addresses for demo
const mockAddresses: Address[] = [
  {
    id: 1,
    user_id: 1,
    address_line_1: '123 Main Street',
    address_line_2: 'Apt 4B',
    city: 'Sydney',
    state_province_region: 'New South Wales',
    is_default: true,
    address_nickname: 'Home',
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 2,
    user_id: 1,
    address_line_1: '456 Business Ave',
    address_line_2: 'Suite 100',
    city: 'Melbourne',
    state_province_region: 'Victoria',
    is_default: false,
    address_nickname: 'Work',
    created_at: '2024-01-02T00:00:00Z',
    updated_at: '2024-01-02T00:00:00Z',
  },
  {
    id: 3,
    user_id: 1,
    address_line_1: '789 Family Road',
    city: 'Brisbane',
    state_province_region: 'Queensland',
    is_default: false,
    address_nickname: "Mom's House",
    created_at: '2024-01-03T00:00:00Z',
    updated_at: '2024-01-03T00:00:00Z',
  },
];

export const useAddressMock = () => {
  const addresses = ref<Address[]>([...mockAddresses]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Get all addresses
  const getAddresses = () => {
    loading.value = true;
    return new Promise((resolve) => {
      setTimeout(() => {
        loading.value = false;
        resolve({
          data: ref(addresses.value),
          pending: ref(false),
          refresh: refreshAddresses,
        });
      }, 500); // Simulate API delay
    });
  };

  // Get default address
  const getDefaultAddress = () => {
    loading.value = true;
    return new Promise((resolve) => {
      setTimeout(() => {
        loading.value = false;
        const defaultAddr = addresses.value.find(addr => addr.is_default);
        resolve({
          data: ref(defaultAddr || null),
        });
      }, 300);
    });
  };

  // Refresh addresses
  const refreshAddresses = () => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(addresses.value);
      }, 200);
    });
  };

  // Add new address
  const createAddress = (addressData: any) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const newAddress: Address = {
          id: Math.max(...addresses.value.map(a => a.id || 0)) + 1,
          user_id: 1,
          ...addressData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        // If this is set as default, unset others
        if (addressData.is_default) {
          addresses.value.forEach(addr => addr.is_default = false);
        }

        addresses.value.push(newAddress);
        resolve({ data: newAddress, error: null });
      }, 500);
    });
  };

  // Update address
  const updateAddress = (id: number, addressData: any) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const index = addresses.value.findIndex(addr => addr.id === id);
        if (index !== -1) {
          // If this is set as default, unset others
          if (addressData.is_default) {
            addresses.value.forEach(addr => addr.is_default = false);
          }
          
          addresses.value[index] = {
            ...addresses.value[index],
            ...addressData,
            updated_at: new Date().toISOString(),
          };
          resolve({ data: addresses.value[index], error: null });
        } else {
          resolve({ data: null, error: 'Address not found' });
        }
      }, 500);
    });
  };

  // Delete address
  const deleteAddress = (id: number) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const index = addresses.value.findIndex(addr => addr.id === id);
        if (index !== -1) {
          addresses.value.splice(index, 1);
          resolve({ data: true, error: null });
        } else {
          resolve({ data: null, error: 'Address not found' });
        }
      }, 500);
    });
  };

  // Set default address
  const setDefaultAddress = (id: number) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        // Unset all defaults
        addresses.value.forEach(addr => addr.is_default = false);
        
        // Set new default
        const address = addresses.value.find(addr => addr.id === id);
        if (address) {
          address.is_default = true;
          resolve({ data: address, error: null });
        } else {
          resolve({ data: null, error: 'Address not found' });
        }
      }, 300);
    });
  };

  return {
    addresses: readonly(addresses),
    loading: readonly(loading),
    error: readonly(error),
    getAddresses,
    getDefaultAddress,
    createAddress,
    updateAddress,
    deleteAddress,
    setDefaultAddress,
    refreshAddresses,
  };
}; 